import type { JSX } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import router from 'next/router';
import Link from 'next/link';
import { jwtDecode } from 'jwt-decode';
import { PublicClientApplication } from '@azure/msal-browser';

import { PageWrap } from '../components/Main';
import Register from '../components/layouts/Register';
import { Button, FormField, Input, Text, View } from '../components/ui';
import { AlertIcon, IconWrapper, WarningAlertIcon } from '../components/Icons';

import type {
  AuthenticateMutation,
  CurrentUserQuery,
  GoogleAuthenticateMutation,
  MicrosoftAuthenticateMutation,
} from '../graphql';
import {
  AuthenticateDocument,
  CurrentUserDocument,
  GoogleAuthenticateDocument,
  MicrosoftAuthenticateDocument,
  useCurrentUserQuery,
} from '../graphql';
import { apolloClient } from '../lib/init-apollo';
import type { JWTToken } from '../lib/cookieParsers';
import { getParsedToken, setToken } from '../lib/cookieParsers';
import { setUser } from '../state/zustand';
import type { CredentialResponse } from '../components/signin/GoogleLogin';
import GoogleLogin from '../components/signin/GoogleLogin';
import MicrosoftLogoSvg from '../images/ms_signin_light.svg';

const googleClientId = process.env.GOOGLE_CLIENT_ID ?? '';
const azureADClientId = process.env.AZUREAD_CLIENT_ID;
const showSSO = googleClientId && azureADClientId;

type User = CurrentUserQuery['currentUser'];

const isPlanner = (user: User) =>
  user?.userRights?.nodes.some((node) => node?.createAndEditVoyages);

const redirect = (user: User) => {
  router.replace(isPlanner(user) ? '/planning' : '/operations');
};

const handleToken = async (jwtToken: string, setState: (s: State) => void) => {
  if (!jwtToken) {
    throw new Error('Invalid credentials');
  }

  setToken(jwtToken);

  setState(State.LOADING);

  const {
    data: { currentUser, myMail, currentUserIsSystemAdmin },
  } = await apolloClient!.query<CurrentUserQuery>({
    fetchPolicy: 'no-cache',
    query: CurrentUserDocument,
  });

  if (!currentUser) {
    throw new Error('Invalid credentials');
  }

  await apolloClient?.logout(); // Clear apollo cache, websocket and apollo instance

  const googleFont =
    typeof document !== 'undefined' && document.getElementById('google_font');
  if (googleFont) {
    googleFont.setAttribute('media', 'print');
    googleFont.removeAttribute('media');
  }

  setUser({
    ...currentUser,
    jwt: jwtDecode<JWTToken>(jwtToken),
    myMail: myMail ?? undefined,
    currentUserIsSystemAdmin: currentUserIsSystemAdmin,
  });

  void redirect(currentUser);
};

type ErrorMessage = string;

function getErrorMessage({ message }: Error): ErrorMessage {
  if (message.includes('Account does not exist')) {
    return 'Invalid credentials';
  }
  if (message.includes('Failed to fetch')) {
    return 'Unable to connect to server';
  }
  return message;
}

enum State {
  INITIAL,
  SIGNING_IN,
  LOADING,
}

const SessionExpired = dynamic(
  () =>
    Promise.resolve(() => (
      <View
        width="100%"
        bg="#ffff0036"
        p={3}
        my={4}
        alignItems="flex-start"
        flexDirection="row"
      >
        <WarningAlertIcon />
        <Text color="#6D6101" ml={3}>
          Your session has expired. Please log in again.
        </Text>
      </View>
    )),
  { ssr: false },
);

const Authenticate = ({
  isSessionExpired,
}: {
  isSessionExpired: boolean;
}): JSX.Element => {
  const [state, setState] = useState<State | ErrorMessage>(State.INITIAL);

  const handleSignIn = useCallback(
    async (
      e:
        | React.FormEvent<HTMLFormElement>
        | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      e.preventDefault();
      e.stopPropagation();
      setState(State.SIGNING_IN);

      try {
        const form = document.querySelector('form') as HTMLFormElement;
        const formData = new FormData(form);
        const email = formData.get('email');
        const password = formData.get('password');

        if (typeof email !== 'string' || typeof password !== 'string') {
          throw new Error('email or password is not a string');
        }

        const authRes = await apolloClient!.mutate<AuthenticateMutation>({
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              email: email.toLowerCase(),
              password,
            },
          },
          mutation: AuthenticateDocument,
        });

        const token = authRes.data?.authenticate?.jwtToken;
        await handleToken(token, setState);
      } catch (err) {
        setState(getErrorMessage(err));
      }
    },
    [],
  );

  const handleGoogleAuthSuccess = useCallback(
    async (response: CredentialResponse) => {
      if (!response.credential) {
        setState('No credentials returned');
        return;
      }
      setState(State.SIGNING_IN);

      try {
        const backendResponse =
          await apolloClient!.mutate<GoogleAuthenticateMutation>({
            fetchPolicy: 'no-cache',
            variables: {
              input: { idToken: response.credential },
            },
            mutation: GoogleAuthenticateDocument,
          });

        const token = backendResponse.data?.googleAuthenticate?.jwtToken;
        await handleToken(token, setState);
      } catch (err) {
        setState(getErrorMessage(err));
      }
    },
    [],
  );

  const handleMicrosoftLogin = useCallback(async () => {
    try {
      setState(State.SIGNING_IN);

      const instance = new PublicClientApplication({
        auth: {
          clientId: azureADClientId || '',
          redirectUri: '/',
        },
      });
      await instance.initialize();
      const { idToken } = await instance.loginPopup({
        scopes: ['email'],
        prompt: 'select_account',
      });

      const backendResponse =
        await apolloClient!.mutate<MicrosoftAuthenticateMutation>({
          fetchPolicy: 'no-cache',
          variables: { input: { idToken } },
          mutation: MicrosoftAuthenticateDocument,
        });

      const token = backendResponse.data?.microsoftAuthenticate?.jwtToken;
      await handleToken(token, setState);
    } catch (err) {
      setState(
        err.errorCode !== 'user_cancelled' ? err.message : State.INITIAL,
      );
    }
  }, []);

  const error = typeof state === 'string' && state;

  return (
    <PageWrap>
      <Register>
        <View width="100%">
          <form onSubmit={handleSignIn}>
            <FormField label="Email">
              <Input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Email"
                height="3.125rem"
                required
                minLength={4}
                aria-label="email"
                border="1px solid #ccc"
                marginBottom="12px"
                padding="8px 12px"
                fontSize="1rem"
              />
            </FormField>
            <FormField label="Password">
              <Input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="Password"
                height="3.125rem"
                required
                aria-label="password"
                border="1px solid #ccc"
                marginBottom="12px"
                padding="8px 12px"
                fontSize="1rem"
              />
            </FormField>
            <input type="submit" hidden={true} aria-hidden={true} />
            <View alignItems="flex-end" mb={20}>
              <Link
                href="/forgot-password"
                style={{
                  display: 'inline-block',
                  minWidth: 132,
                  minHeight: 22,
                  lineHeight: 1,
                  textDecoration: 'none',
                  color: '#1c1c1c',
                }}
              >
                Forgot password?
              </Link>
            </View>
            {isSessionExpired && <SessionExpired />}
            {error && (
              <View
                width="100%"
                bg="#ffd6c9"
                p={3}
                my={4}
                alignItems="flex-start"
                flexDirection="row"
              >
                <AlertIcon />
                <Text color="#FC4A15" ml={3}>
                  Error: {error}
                </Text>
              </View>
            )}
            <Button
              onPress={handleSignIn}
              disabled={state !== State.INITIAL && !error}
              width="100%"
              aria-label="login"
              backgroundColor="#ffe552"
              color="#000000"
              border="1px solid #91a9ca80"
            >
              {state === State.LOADING
                ? 'Success. Loading...'
                : state === State.SIGNING_IN
                  ? 'Checking..'
                  : 'Login'}
            </Button>
          </form>
        </View>
        {showSSO && (
          // These can cause a bit of layout shift,
          // setting the height to the value that it would otherwise settle at ensures minimal shifts
          <View
            width="100%"
            alignItems="center"
            style={{
              height: 85,
            }}
          >
            <IconWrapper
              style={{
                width: 200,
                height: 43,
              }}
            >
              <img
                src={MicrosoftLogoSvg}
                width={200}
                height={38}
                onClick={handleMicrosoftLogin}
              />
            </IconWrapper>
            <GoogleLogin
              style={{ height: 38 }}
              clientId={googleClientId}
              onSuccess={handleGoogleAuthSuccess}
            />
          </View>
        )}
      </Register>
    </PageWrap>
  );
};

const isBrowser =
  typeof window !== 'undefined' && process.env.JEST_WORKER_ID === undefined;

let prefetch = true;

const SignIn = () => {
  const jwtToken = useMemo(getParsedToken, []);
  const sessionExpired = jwtToken ? jwtToken.exp * 1000 <= Date.now() : false;
  const { data } = useCurrentUserQuery({
    fetchPolicy: 'no-cache',
    skip: !jwtToken || sessionExpired,
  });
  const user = data?.currentUser;
  if (user) {
    void redirect(user);
  }

  if (prefetch && isBrowser) {
    void router.prefetch('/planning');
    void router.prefetch('/operations');
    prefetch = false;
  }

  return <Authenticate isSessionExpired={sessionExpired} />;
};

SignIn.publicPage = true;
export default SignIn;
